import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import { dashboardAPI, propertiesAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import ActivityTimeline from '../../components/common/ActivityTimeline';
import { 
  Building2, 
  FileText, 
  CheckCircle2, 
  Clock, 
  AlertCircle,
  ChevronRight,
  Plus,
  Loader2
} from 'lucide-react';

const PropertyContributorDashboard = () => {
  const { user } = useAuth();
  const { t } = useI18n();
  const [stats, setStats] = useState(null);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, propsRes] = await Promise.all([
        dashboardAPI.propertyContributor(),
        propertiesAPI.list()
      ]);
      setStats(statsRes.data);
      setProperties(propsRes.data);
    } catch (error) {
      setStats({
        profile_completion: 30,
        verification_status: 'pending',
        total_properties: 0,
        submitted_properties: 0,
        pending_review: 0,
        approved_properties: 0,
        total_documents: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: { variant: 'outline', className: 'text-muted-foreground', label: 'Borrador' },
      submitted: { variant: 'outline', className: 'border-blue-500/50 text-blue-500', label: 'Enviado' },
      initial_review: { variant: 'outline', className: 'border-blue-500/50 text-blue-500', label: 'En Revisión' },
      missing_info: { variant: 'outline', className: 'border-orange-500/50 text-orange-500', label: 'Info Requerida' },
      legal_review: { variant: 'outline', className: 'border-purple-500/50 text-purple-500', label: 'Revisión Legal' },
      approved: { variant: 'default', className: 'bg-emerald-500', label: 'Aprobado' },
      rejected: { variant: 'destructive', className: '', label: 'Rechazado' },
    };
    const style = styles[status] || styles.draft;
    return <Badge variant={style.variant} className={style.className}>{style.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" strokeWidth={1.5} />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="contributor-dashboard">
      {/* Welcome Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="heading-2 mb-2">
            {t('dashboard.welcome')}, {user?.name?.split(' ')[0]}
          </h1>
          <p className="body-text">
            Gestione sus inmuebles y siga el estado de sus aportaciones.
          </p>
        </div>
        <Link to="/app/properties/new">
          <Button className="btn-primary gap-2">
            <Plus className="h-4 w-4" strokeWidth={1.5} />
            Aportar Inmueble
          </Button>
        </Link>
      </div>

      {/* Profile Completion Alert */}
      {stats?.profile_completion < 100 && (
        <Card className="border-amber-500/20 bg-amber-500/5">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <div>
                  <p className="font-medium text-amber-500 mb-1">Complete su perfil</p>
                  <p className="text-sm text-muted-foreground">
                    Para aportar inmuebles necesita completar su perfil. Actualmente está al {stats.profile_completion}%.
                  </p>
                </div>
              </div>
              <Link to="/app/profile">
                <Button variant="outline" size="sm" className="gap-2 border-amber-500/30 text-amber-500 hover:bg-amber-500/10">
                  Completar Perfil
                  <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="card-dashboard">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-normal text-muted-foreground">
              Inmuebles Totales
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <Building2 className="h-8 w-8 text-primary" strokeWidth={1.5} />
              <span className="text-3xl font-light">{stats?.total_properties || 0}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="card-dashboard">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-normal text-muted-foreground">
              En Revisión
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <Clock className="h-8 w-8 text-amber-500" strokeWidth={1.5} />
              <span className="text-3xl font-light">{stats?.pending_review || 0}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="card-dashboard">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-normal text-muted-foreground">
              Aprobados
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-8 w-8 text-emerald-500" strokeWidth={1.5} />
              <span className="text-3xl font-light">{stats?.approved_properties || 0}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="card-dashboard">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-normal text-muted-foreground">
              Documentos Subidos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <FileText className="h-8 w-8 text-primary" strokeWidth={1.5} />
              <span className="text-3xl font-light">{stats?.total_documents || 0}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Properties List */}
      <Card className="card-dashboard">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Mis Inmuebles</CardTitle>
          <Link to="/app/properties">
            <Button variant="ghost" size="sm" className="gap-1">
              Ver todos
              <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {properties.length > 0 ? (
            <div className="space-y-4">
              {properties.slice(0, 5).map((prop) => (
                <Link 
                  key={prop.property_id} 
                  to={`/app/properties/${prop.property_id}`}
                  className="flex items-center justify-between p-4 rounded-sm border border-border hover:border-primary/30 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <Building2 className="h-10 w-10 text-muted-foreground" strokeWidth={1} />
                    <div>
                      <p className="font-medium mb-1">{prop.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {prop.city}, {prop.state} • {prop.property_type}
                      </p>
                    </div>
                  </div>
                  {getStatusBadge(prop.status)}
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" strokeWidth={1} />
              <p className="text-muted-foreground mb-4">No ha agregado ningún inmueble aún</p>
              <Link to="/app/properties/new">
                <Button className="btn-primary gap-2">
                  <Plus className="h-4 w-4" strokeWidth={1.5} />
                  Agregar Primer Inmueble
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-lg">Acciones Rápidas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/app/properties/new" className="flex items-center justify-between p-3 rounded-sm hover:bg-muted transition-colors">
              <span className="text-sm">Aportar Nuevo Inmueble</span>
              <ChevronRight className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
            </Link>
            <Link to="/app/documents" className="flex items-center justify-between p-3 rounded-sm hover:bg-muted transition-colors">
              <span className="text-sm">Subir Documentos</span>
              <ChevronRight className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
            </Link>
            <Link to="/app/profile" className="flex items-center justify-between p-3 rounded-sm hover:bg-muted transition-colors">
              <span className="text-sm">Completar Perfil</span>
              <ChevronRight className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
            </Link>
          </CardContent>
        </Card>

        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-lg">Proceso de Aportación</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <span className="h-6 w-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs">1</span>
              <span className="text-sm">Registre su inmueble con información básica</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="h-6 w-6 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-xs">2</span>
              <span className="text-sm text-muted-foreground">Suba los documentos requeridos</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="h-6 w-6 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-xs">3</span>
              <span className="text-sm text-muted-foreground">Nuestro equipo revisará su solicitud</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="h-6 w-6 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-xs">4</span>
              <span className="text-sm text-muted-foreground">Reciba y revise términos indicativos</span>
            </div>
          </CardContent>
        </Card>
        
        {/* Activity Timeline */}
        <ActivityTimeline 
          title="Tu Actividad Reciente"
          emptyMessage="Aún no hay actividad registrada. Proponga un inmueble para comenzar."
          limit={5}
        />
      </div>
    </div>
  );
};

export default PropertyContributorDashboard;
